import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Facility } from './Facility'
import type { POSTRelatedResponseType } from '../../property/infrastructure/HTTPPropertyRepository/schemas/related'
import { FacilityCodeGroup } from './FacilityCodeGroup'
import { FacilityID } from './FacilityID'
import type { SearchProperty } from '../../property/infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'
import type { GETAvailabilityPropertyIdResponseType } from '../../booking/infrastructure/HTTPBookingRepository/schemas/v4/availability/property/:id'

const FEATURED = 'Featured'
const MISSING_TRANSLATION = 'MISSING TRANSLATION'

type NonEmptyArray<T> = [T, ...T[]]

export const ListOfFacilityValidation = z.object({
  list: z.instanceof(Facility, { message: '[ListOfFacility] list is required' }).array(),
})

export class ListOfFacility extends Model {
  static create({ list }: z.infer<typeof ListOfFacilityValidation>) {
    ListOfFacilityValidation.parse({ list })
    return new ListOfFacility(list)
  }

  static fromRelatedApi({
    json,
  }: {
    json: POSTRelatedResponseType['properties'][number]['facilities']
  }) {
    return ListOfFacility.create({
      list: json.map(facilityJSON =>
        Facility.create({
          codeGroup: FacilityCodeGroup.create({ value: facilityJSON.code_group }),
          id: FacilityID.create({ value: facilityJSON.id }),
        })
      ),
    })
  }

  static fromJSON({ json }: { json: ReturnType<ListOfFacility['toJSON']> }) {
    return ListOfFacility.create({
      list: json.list.map(facilityJSON =>
        Facility.create({
          codeGroup: FacilityCodeGroup.create({ value: facilityJSON.codeGroup }),
          id: FacilityID.create({ value: facilityJSON.id }),
        })
      ),
    })
  }

  static fromJSONOrEmpty({ json }: { json?: ReturnType<ListOfFacility['toJSON']> }) {
    try {
      if (!json) {
        return ListOfFacility.empty()
      }
      return ListOfFacility.fromJSON({ json })
    } catch {
      return ListOfFacility.empty()
    }
  }

  static fromSerpApiLegacyOrEmpty({ json }: { json: SearchProperty['facilities'] }) {
    try {
      if (!json) {
        return ListOfFacility.empty()
      }

      const facilities = Array.from(json)
        .filter(facilityJson => facilityJson?.name)
        .map(facilityJson => {
          return Facility.fromSerpApiLegacy({ id: facilityJson.name })
        })

      return ListOfFacility.create({
        list: facilities,
      })
    } catch (error) {
      console.error(error)
      return ListOfFacility.empty()
    }
  }

  static fromAvailabilityApi({
    availability,
    json,
  }: {
    availability: GETAvailabilityPropertyIdResponseType[string]['availability'][number]
    json: GETAvailabilityPropertyIdResponseType
  }) {
    // 1. Look up room_name in room_content -> return facility_groups[featured]:
    try {
      const roomName = availability.room_name

      const roomNameContent = json.room_content[roomName]

      const facilitiesGroupFromName = roomNameContent?.facility_groups.find(
        group => group.description === FEATURED
      )

      const facilitiesFromName = facilitiesGroupFromName?.facilities

      if (facilitiesFromName)
        return ListOfFacility.create({
          list: facilitiesFromName.map(facility =>
            Facility.fromPrimitive({ codeGroup: facility.code_group, id: facility.name })
          ),
        })

      // 2. Look up room_code in facilities_room_codes of other rooms that have facilities -> return their facility_groups[featured]:
      const roomCode = availability.room_code

      const otherRoomsWithFacilities = Object.keys(json.room_content).filter(roomNameKey => {
        const room = json.room_content[roomNameKey]
        const roomCodes = room?.facilities_room_codes ?? []
        const hasFacilities = room?.facility_groups?.length > 0
        const lowerCaseCode = roomCode.toLowerCase()
        const isCodeInRoomCodes = roomCodes.some(
          (roomCodeInArray: string) => roomCodeInArray.toLowerCase() === lowerCaseCode
        )
        return isCodeInRoomCodes && hasFacilities
      })

      if (otherRoomsWithFacilities.length) {
        const matchedRoomName = otherRoomsWithFacilities[0]
        const roomCodeContent = json.room_content[matchedRoomName]
        const facilitiesGroupFromCode = roomCodeContent?.facility_groups.find(
          group => group.description === FEATURED
        )
        const facilitiesFromCode = facilitiesGroupFromCode?.facilities

        if (facilitiesFromCode)
          return ListOfFacility.create({
            list: facilitiesFromCode.map(facility =>
              Facility.fromPrimitive({ codeGroup: facility.code_group, id: facility.name })
            ),
          })
      }

      // 3. Look up room_code inside MISSING TRANSLATION:
      const facilitiesGroupFromMissingTranslation = json.room_content?.[
        MISSING_TRANSLATION
      ]?.facility_groups.find(group => group.description === FEATURED)

      const facilitiesFromMissingTranslation =
        facilitiesGroupFromMissingTranslation?.facilities?.filter(
          facility => facility.room_code === roomCode
        )

      if (facilitiesFromMissingTranslation)
        return ListOfFacility.create({
          list: facilitiesFromMissingTranslation.map(facility =>
            Facility.fromPrimitive({ codeGroup: facility.code_group, id: facility.name })
          ),
        })

      // 4. Look up facilities in defaults:
      const facilitiesGroupFromDefaults = json.defaults?.facility_groups.find(
        group => group.description === FEATURED
      )

      const facilitiesFromDefaults = facilitiesGroupFromDefaults?.facilities

      if (facilitiesFromDefaults)
        return ListOfFacility.create({
          list: facilitiesFromDefaults.map(facility =>
            Facility.fromPrimitive({ codeGroup: facility.code_group, id: facility.name })
          ),
        })

      // 5. If nothing is found, return empty:
      return ListOfFacility.empty()
    } catch {
      return ListOfFacility.empty()
    }
  }

  static empty() {
    return new ListOfFacility([])
  }

  public readonly list: Facility[]

  constructor(list: Facility[]) {
    super()
    this.list = list
  }

  toJSON() {
    return { list: this.list.map(item => item.toJSON()) }
  }

  map<T>(fn: (facility: Facility) => T) {
    return this.list.map(facility => fn(facility))
  }

  filter(fn: (facility: Facility) => boolean) {
    return new ListOfFacility(this.list.filter(facility => fn(facility)))
  }

  take(end: number) {
    return new ListOfFacility(this.list.slice(0, end))
  }

  uniqueById() {
    return new ListOfFacility(
      this.list.filter((facility, index, arr) => arr.findIndex(f => f.id === facility.id) === index)
    )
  }

  hasFacilities(): this is this & { list: NonEmptyArray<Facility> } {
    return this.list.length !== 0
  }

  isEmpty(): this is this & { list: [] } {
    return this.list.length === 0
  }
}
